import {
  type CalibrationFit,
  type CalibrationProblem,
  fitCalibration,
  formatConcentration,
  standardReadings,
  unknownAbsorbance,
} from './instrumentalAnalysis';
import {
  CONSTANT_MASS_TOLERANCE_G,
  type GravimetricProblem,
  buildGravimetricModel,
  formatMass,
  formatPercent,
} from './instrumentalGravimetric';
import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError, formatModelNumber } from './modelInputContract';

/**
 * Explore calculations for the Instrumental Analysis Lab (CT-INTERACT, chemistry/CLAUDE.md
 * "Interactive calculation standard"): a typed absorbance read off the reviewed calibration line,
 * and a gravimetric result from the learner's own weighings. Both fail closed at the model limit.
 */

const SUPERSCRIPT: Record<string, string> = { '-': '⁻', 0: '⁰', 1: '¹', 2: '²', 3: '³', 4: '⁴', 5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹' };

/** 4.217 × 10⁻³ style scientific notation with four significant figures. */
export function scientific(value: number): string {
  const [mantissa, exponent] = value.toExponential(3).split('e');
  return `${mantissa} × 10${String(Number(exponent)).split('').map((character) => SUPERSCRIPT[character]).join('')}`;
}

// ---- Colorimetry and AAS: concentration from a typed absorbance ----

export type CalibrationExploreInput = 'absorbance' | 'dilution';
export type CalibrationExploreSetup = Record<CalibrationExploreInput, number>;

export const CALIBRATION_EXPLORE_FIELDS: ModelField<CalibrationExploreInput>[] = [
  { key: 'absorbance', label: 'Absorbance', unit: 'A', min: 0, max: 2, step: 0.001 },
  { key: 'dilution', label: 'Dilution factor', unit: '×', min: 1, max: 100, step: 1 },
];

export function defaultCalibrationSetup(problem: CalibrationProblem): CalibrationExploreSetup {
  return { absorbance: unknownAbsorbance(problem), dilution: problem.dilutionFactor };
}

export interface CalibrationExploreResult {
  fit: CalibrationFit;
  inRange: boolean;
  /** Concentration of the measured (diluted) solution; null outside the standards. */
  measured: number | null;
  sample: number | null;
  steps: WorkedStep[];
  result: string;
  limitNote: string | null;
}

/** Reads a typed absorbance off the line fitted to every reviewed standard. Throws on invalid input. */
export function calculateCalibrationExplore(problem: CalibrationProblem, setup: CalibrationExploreSetup): CalibrationExploreResult {
  const error = firstModelError(CALIBRATION_EXPLORE_FIELDS, setup);
  if (error) throw new Error(error);
  const fit = fitCalibration(standardReadings(problem));
  if (!fit) throw new Error('This problem has no reviewed linear calibration, so the model cannot read a concentration.');
  const unit = problem.unit;
  const lowA = fit.slope * fit.minConcentration + fit.intercept;
  const highA = fit.slope * fit.maxConcentration + fit.intercept;
  const a = setup.absorbance;
  const inRange = a >= lowA && a <= highA;
  const measured = (a - fit.intercept) / fit.slope;
  const sample = measured * setup.dilution;
  const sign = fit.intercept < 0 ? '−' : '+';
  const minus = fit.intercept < 0 ? '+' : '−';

  const steps: WorkedStep[] = [
    {
      id: 'line',
      title: `Calibration line from ${fit.pointCount} standards`,
      formula: 'A = m × c + b',
      working: `A = ${fit.slope.toFixed(4)} × c ${sign} ${Math.abs(fit.intercept).toFixed(4)} · R² = ${fit.rSquared.toFixed(4)}`,
      status: 'done',
    },
    {
      id: 'range',
      title: 'Is A inside the standards?',
      formula: 'A(lowest standard) ≤ A ≤ A(highest standard)',
      working: inRange
        ? `${lowA.toFixed(3)} ≤ ${a.toFixed(3)} ≤ ${highA.toFixed(3)}: inside`
        : `${a.toFixed(3)} is ${a > highA ? `above the highest standard (${highA.toFixed(3)})` : `below the lowest standard (${lowA.toFixed(3)})`}: outside`,
      status: inRange ? 'done' : 'current',
    },
    {
      id: 'measured',
      title: 'Concentration of the measured solution',
      formula: 'c = (A − b) ÷ m',
      working: `c = (${a.toFixed(3)} ${minus} ${Math.abs(fit.intercept).toFixed(4)}) ÷ ${fit.slope.toFixed(4)} = ${formatConcentration(measured)} ${unit}`,
      status: inRange ? 'done' : 'not-reached',
    },
    {
      id: 'sample',
      title: 'Concentration in the original sample',
      formula: 'c(sample) = c × dilution factor',
      working: `c = ${formatConcentration(measured)} × ${formatModelNumber(setup.dilution)} = ${formatConcentration(sample)} ${unit}`,
      status: inRange ? 'current' : 'not-reached',
    },
  ];

  return {
    fit,
    inRange,
    measured: inRange ? measured : null,
    sample: inRange ? sample : null,
    steps,
    result: inRange ? `Result: ${formatConcentration(sample)} ${unit} in the original sample` : 'Result: no value reported',
    limitNote: inRange
      ? null
      : a > highA
        ? 'Above the highest standard the model refuses to extend the line: Beer–Lambert behaviour there has not been checked. Dilute the sample so it falls between the standards and measure again.'
        : 'Below the lowest standard the model refuses to extend the line. Prepare lower standards, use a longer cell or a more sensitive method, then measure again.',
  };
}

// ---- Gravimetric analysis from the learner's own weighings ----

export type GravimetricInput = 'sampleMassG' | 'crucibleG' | 'weighing1G' | 'weighing2G' | 'weighing3G';
export type GravimetricSetup = Record<GravimetricInput, number>;
export type WeighingCount = 2 | 3;

const MASS_STEP = 0.0001;
const WEIGHING_KEYS: GravimetricInput[] = ['weighing1G', 'weighing2G', 'weighing3G'];

export function gravimetricExploreFields(count: WeighingCount): ModelField<GravimetricInput>[] {
  return [
    { key: 'sampleMassG', label: 'Sample mass', unit: 'g', min: 0.05, max: 5, step: MASS_STEP },
    { key: 'crucibleG', label: 'Empty crucible', unit: 'g', min: 5, max: 60, step: MASS_STEP },
    ...WEIGHING_KEYS.slice(0, count).map((key, index) => ({
      key,
      label: `Crucible + solid, weighing ${index + 1}`,
      unit: 'g',
      min: 5,
      max: 80,
      step: MASS_STEP,
    })),
  ];
}

/** The problem's reviewed sample, crucible and last weighings, as the default and the shortcut. */
export function defaultGravimetricSetup(problem: GravimetricProblem): GravimetricSetup {
  const { weighings } = buildGravimetricModel(problem);
  const last = weighings.slice(-3);
  return { sampleMassG: problem.sampleMassG, crucibleG: problem.emptyCrucibleG, weighing1G: last[0], weighing2G: last[1], weighing3G: last[2] };
}

export interface GravimetricExploreResult {
  weighings: number[];
  constant: boolean;
  precipitateG: number | null;
  percent: number | null;
  steps: WorkedStep[];
  result: string;
  limitNote: string | null;
}

/** Constant-mass check, then mass → moles → ratio → mass → %. Throws on invalid input (fail closed). */
export function calculateGravimetricExplore(problem: GravimetricProblem, setup: GravimetricSetup, count: WeighingCount): GravimetricExploreResult {
  const error = firstModelError(gravimetricExploreFields(count), setup);
  if (error) throw new Error(error);
  const weighings = WEIGHING_KEYS.slice(0, count).map((key) => setup[key]);
  weighings.forEach((mass, index) => {
    if (mass <= setup.crucibleG) {
      throw new Error(`Weighing ${index + 1} (${formatMass(mass)} g) is not more than the empty crucible (${formatMass(setup.crucibleG)} g), so there is no solid to weigh.`);
    }
  });
  const model = buildGravimetricModel(problem);
  const last = weighings[weighings.length - 1];
  const previous = weighings[weighings.length - 2];
  const change = last - previous;
  const constant = Math.abs(change) <= CONSTANT_MASS_TOLERANCE_G + 1e-9;
  const gained = change > CONSTANT_MASS_TOLERANCE_G + 1e-9;

  const precipitateG = last - setup.crucibleG;
  const nWeighed = precipitateG / model.weighedMolarMass;
  const nAnalyte = nWeighed * problem.analytePerWeighedForm;
  const analyteG = nAnalyte * model.analyteMolarMass;
  const percent = (analyteG / setup.sampleMassG) * 100;
  const impossible = percent > 100;
  const after = (status: WorkedStep['status']): WorkedStep['status'] => (constant ? status : 'not-reached');

  const steps: WorkedStep[] = [
    {
      id: 'constant',
      title: 'Constant mass?',
      formula: `|last − previous| ≤ ${CONSTANT_MASS_TOLERANCE_G} g`,
      working: `|${formatMass(last)} − ${formatMass(previous)}| = ${formatMass(Math.abs(change))} g ${constant ? `≤ ${CONSTANT_MASS_TOLERANCE_G} g: constant mass` : `> ${CONSTANT_MASS_TOLERANCE_G} g: not constant yet`}`,
      status: constant ? 'done' : 'current',
    },
    {
      id: 'precipitate',
      title: `Mass of ${problem.weighedForm}`,
      formula: `m(${problem.weighedForm}) = last weighing − empty crucible`,
      working: `m = ${formatMass(last)} − ${formatMass(setup.crucibleG)} = ${formatMass(precipitateG)} g`,
      status: after('done'),
    },
    {
      id: 'moles',
      title: `Moles of ${problem.weighedForm}`,
      formula: `n = m ÷ M(${problem.weighedForm})`,
      working: `n = ${formatMass(precipitateG)} ÷ ${model.weighedMolarMass.toFixed(2)} = ${scientific(nWeighed)} mol`,
      status: after('done'),
    },
    {
      id: 'ratio',
      title: `Moles of ${problem.analyte}`,
      formula: `n(${problem.analyte}) = ${problem.analytePerWeighedForm} × n(${problem.weighedForm})`,
      working: `n = ${problem.analytePerWeighedForm} × ${scientific(nWeighed)} = ${scientific(nAnalyte)} mol`,
      status: after('done'),
    },
    {
      id: 'analyte',
      title: `Mass of ${problem.analyte}`,
      formula: `m = n × M(${problem.analyte})`,
      working: `m = ${scientific(nAnalyte)} × ${model.analyteMolarMass.toFixed(2)} = ${formatMass(analyteG)} g`,
      status: after('done'),
    },
    {
      id: 'percent',
      title: `% ${problem.analyte} by mass`,
      formula: `% = m(${problem.analyte}) ÷ sample mass × 100`,
      working: `% = ${formatMass(analyteG)} ÷ ${formatMass(setup.sampleMassG)} × 100 = ${formatPercent(percent)}%`,
      status: after('current'),
    },
  ];

  const reportable = constant && !impossible;
  let limitNote: string | null = null;
  if (gained) {
    limitNote = 'The mass went up between the last two weighings. A dried solid should not gain mass, so check the balance and the cooling (a warm crucible or moist air). The model reports no percentage.';
  } else if (!constant) {
    limitNote = 'Not at constant mass yet: water may still be in the solid. Heat, cool and weigh again. The model reports no percentage until two weighings agree.';
  } else if (impossible) {
    limitNote = `${formatPercent(percent)}% is more than 100%, which is impossible. Check the sample, crucible and weighing masses. The model reports no percentage.`;
  }

  return {
    weighings,
    constant,
    precipitateG: constant ? precipitateG : null,
    percent: reportable ? percent : null,
    steps,
    result: reportable ? `Result: ${formatPercent(percent)}% ${problem.analyte} by mass` : 'Result: no percentage reported',
    limitNote,
  };
}

// ---- Teaching goals (CT-INTERACT C7) ----

/** Relative band a "same value" calibration task accepts: ± 5 %. */
export const CALIBRATION_CHALLENGE_TOLERANCE = 0.05;
/** Relative band the gravimetric "same percentage" task accepts: ± 2 %. */
export const GRAVIMETRIC_CHALLENGE_TOLERANCE = 0.02;
/** The "bigger sample" task needs at least this multiple of the problem's sample mass. */
export const GRAVIMETRIC_SAMPLE_MULTIPLE = 1.9;

const withinRelative = (value: number, target: number, tolerance: number) => Math.abs(value - target) <= tolerance * Math.abs(target);

/** What a calibration task check reads: the problem, the learner's valid setup and the model result. */
export interface CalibrationChallengeInput {
  problem: CalibrationProblem;
  setup: CalibrationExploreSetup;
  result: CalibrationExploreResult;
}

export const CALIBRATION_QUESTION = 'How does a calibration line turn an absorbance into a concentration, and why do you sometimes dilute first?';

/** Sample concentration at the problem's own absorbance and dilution, read off the line even outside it. */
export function calibrationReferenceSample(problem: CalibrationProblem, fit: CalibrationFit): number {
  const setup = defaultCalibrationSetup(problem);
  return ((setup.absorbance - fit.intercept) / fit.slope) * setup.dilution;
}

export const CALIBRATION_CHALLENGES: ExploreChallenge<CalibrationChallengeInput>[] = [
  {
    id: 'top-standard',
    prompt: 'Find the largest absorbance the model still reads: make the measured concentration equal the highest standard (± 5 %).',
    hint: 'Slide the absorbance up until the point reaches the top end of the line.',
    isMet: ({ result }) =>
      result.measured !== null && withinRelative(result.measured, result.fit.maxConcentration, CALIBRATION_CHALLENGE_TOLERANCE),
    why: 'Beer–Lambert, A = εcl, makes absorbance rise in step with concentration, but only the part of the line between the standards has been checked. The highest standard is the end of the trusted range.',
  },
  {
    id: 'outside-standards',
    prompt: 'Type a new absorbance outside the standards (above the highest or below the lowest). What does the model report?',
    isMet: ({ problem, setup, result }) => !result.inRange && setup.absorbance !== defaultCalibrationSetup(problem).absorbance,
    why: 'Extending the line is a guess: at high absorbance very little light reaches the detector and real readings curve away from a straight line. So the model reports no value until the reading is inside the standards.',
  },
  {
    id: 'dilute-more',
    prompt: 'Dilute the sample more: at least double the dilution factor, then type the smaller absorbance that gives the same sample concentration (± 5 %).',
    hint: 'Doubling the dilution roughly halves the absorbance.',
    isMet: ({ problem, setup, result }) =>
      result.sample !== null &&
      setup.dilution >= 2 * problem.dilutionFactor &&
      withinRelative(result.sample, calibrationReferenceSample(problem, result.fit), CALIBRATION_CHALLENGE_TOLERANCE),
    why: 'Diluting lowers the absorbance, and c(sample) = c(measured) × dilution factor undoes the dilution. A 36 mg L⁻¹ copper rinse diluted 10 times reads 3.6 mg L⁻¹, inside the 1–5 mg L⁻¹ standards.',
  },
];

/** What a gravimetric task check reads: the problem, the learner's valid masses and the model result. */
export interface GravimetricChallengeInput {
  problem: GravimetricProblem;
  setup: GravimetricSetup;
  count: WeighingCount;
  result: GravimetricExploreResult;
}

export const GRAVIMETRIC_QUESTION = 'Why do you heat and weigh again until the mass stops changing, and what does the percentage depend on?';

const lastChange = ({ weighings }: GravimetricExploreResult) => weighings[weighings.length - 1] - weighings[weighings.length - 2];

export const GRAVIMETRIC_CHALLENGES: ExploreChallenge<GravimetricChallengeInput>[] = [
  {
    id: 'still-wet',
    prompt: `Show a solid that is not dry yet: make the last weighing more than ${CONSTANT_MASS_TOLERANCE_G} g lighter than the one before.`,
    hint: 'Raise the second-last weighing by a few milligrams.',
    isMet: ({ result }) => lastChange(result) < -CONSTANT_MASS_TOLERANCE_G - 1e-9,
    why: `A mass that is still falling means water is still leaving the solid. Weighing it now would make the percentage too high, so the model waits until two weighings agree within ${CONSTANT_MASS_TOLERANCE_G} g.`,
  },
  {
    id: 'gained-mass',
    prompt: `Make the last weighing more than ${CONSTANT_MASS_TOLERANCE_G} g heavier than the one before. Is that more product?`,
    isMet: ({ result }) => lastChange(result) > CONSTANT_MASS_TOLERANCE_G + 1e-9,
    why: 'No: heating cannot make more precipitate. A dried solid that gains mass has taken up water from the air or was weighed warm, so the reading is rejected, not used.',
  },
  {
    id: 'double-sample',
    prompt: `Use about twice the sample mass (at least ${GRAVIMETRIC_SAMPLE_MULTIPLE} ×) and type weighings that give the same percentage (± 2 %) at constant mass.`,
    hint: 'Twice the sample gives twice the precipitate: add the same mass of solid again to the crucible readings.',
    isMet: ({ problem, setup, result }) =>
      result.percent !== null &&
      setup.sampleMassG >= GRAVIMETRIC_SAMPLE_MULTIPLE * problem.sampleMassG &&
      withinRelative(result.percent, buildGravimetricModel(problem).percent, GRAVIMETRIC_CHALLENGE_TOLERANCE),
    why: 'The percentage is a property of the material, not of how much you weigh: twice the sample gives twice the precipitate and the same %. For 0.5000 g of pool salt, about 1.2088 g of AgCl still gives 59.8 % chloride.',
  },
];
